(() => {
  if (customElements.get('faf-mobile-navigation')) return;

  class FAFRefinedMobileMenu extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.dialog = this.querySelector('.faf-mobile-dialog');
      this.trigger = this.querySelector('[data-faf-open]');
      this.scroller = this.querySelector('[data-faf-menu-scroll]');
      this.rootPanel = this.querySelector('[data-faf-menu-root]');
      if (!this.dialog || !this.trigger || !this.scroller || !this.rootPanel) return;

      this.panels = new Map([...this.querySelectorAll('[data-faf-menu-panel]')]
        .map(panel => [panel.dataset.fafMenuPanel, panel]));
      this.panelButtons = [...this.querySelectorAll('[data-faf-panel-open]')];
      this.desktop = matchMedia('(min-width: 1100px)');
      this.resetPanels();
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.addEventListener('click', () => this.openMenu(), options);
      this.dialog.addEventListener('click', event => this.onClick(event), options);
      this.dialog.addEventListener('keydown', event => this.onTabKeydown(event), options);
      this.dialog.addEventListener('pointerdown', event => {
        this.backdropPointer = event.button === 0 && this.isBackdrop(event) ? event.pointerId : null;
      }, options);
      this.dialog.addEventListener('pointercancel', () => { this.backdropPointer = null; }, options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        if (this.history.length > 1) this.goBack();
        else this.closeMenu('dismiss');
      }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.finishClose('dismiss');
      }, options);
      this.desktop.addEventListener('change', event => {
        if (event.matches) this.closeMenu('resize');
      }, options);
    }

    disconnectedCallback() {
      this.closeMenu('disconnect');
      this.unlockScroll();
      this.listeners?.abort();
      this.backdropPointer = null;
    }

    headerRoot() {
      return this.closest('.shopify-section-header') || this.closest('sticky-header') || this.closest('header');
    }

    openMenu() {
      if (this.desktop.matches || this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      this.resetPanels();
      this.backdropPointer = null;
      this.lockScroll();
      this.sessionActive = true;
      try {
        this.dialog.showModal();
        this.scroller.scrollTop = 0;
        this.scroller.scrollLeft = 0;
        this.trigger.setAttribute('aria-expanded', 'true');
      } catch (error) {
        this.finishClose('dismiss');
      }
    }

    closeMenu(reason = 'dismiss') {
      if (this.dialog?.open) this.dialog.close();
      this.finishClose(reason);
    }

    finishClose(reason) {
      if (!this.sessionActive) return;
      this.sessionActive = false;
      this.unlockScroll();
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.backdropPointer = null;
      if (!this.isConnected) return;
      // Complete focus restoration now; the queued native close event must not steal search focus.
      if (reason === 'dismiss') {
        const destination = this.desktop.matches
          ? this.headerRoot()?.querySelector('.faf-desktop-nav__trigger')
          : this.trigger;
        destination?.focus({ preventScroll: true });
      } else if (reason === 'resize') {
        this.headerRoot()?.querySelector('.faf-desktop-nav__trigger')?.focus({ preventScroll: true });
      }
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const target = event.target;
      if (target.closest('[data-faf-close]')) {
        event.preventDefault();
        this.closeMenu('dismiss');
        return;
      }
      const panelButton = target.closest('[data-faf-panel-open]');
      if (panelButton) {
        event.preventDefault();
        if (!panelButton.disabled) this.openPanel(panelButton);
        return;
      }
      if (target.closest('[data-faf-panel-back]')) {
        event.preventDefault();
        this.goBack();
        return;
      }
      const anchor = target.closest('a[href]');
      const plainClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      if (anchor && plainClick) {
        if (anchor.matches('[data-faf-menu-search]') && !anchor.hasAttribute('download') && (!anchor.target || anchor.target === '_self')) {
          const input = this.headerRoot()?.querySelector('.header__search input[type="search"][name="q"]');
          if (input && !input.disabled && input.isConnected && input.getClientRects().length) {
            event.preventDefault();
            this.closeMenu('search');
            // Keep this inside the user gesture so mobile browsers can show the keyboard.
            input.focus({ preventScroll: true });
            return;
          }
        }
        this.closeMenu('navigate');
        return;
      }
      if (this.isBackdrop(event) && this.backdropPointer !== null && event.detail > 0) {
        this.closeMenu('dismiss');
      }
      this.backdropPointer = null;
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const bounds = this.dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom;
    }

    onTabKeydown(event) {
      if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !this.dialog.open) return;
      // Rebuild for the current panel so hidden departments cannot enter the keyboard loop.
      const candidates = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter(element => {
          if (element.tabIndex < 0 || element.matches(':disabled, [disabled], [aria-disabled="true"]') ||
              element.closest('[hidden], [inert], [aria-hidden="true"]') || !element.getClientRects().length) return false;
          const visibility = getComputedStyle(element).visibility;
          return visibility !== 'hidden' && visibility !== 'collapse';
        })
        .sort((first, second) => (first.tabIndex > 0 ? first.tabIndex : Number.MAX_SAFE_INTEGER) -
          (second.tabIndex > 0 ? second.tabIndex : Number.MAX_SAFE_INTEGER));
      if (!candidates.length) {
        event.preventDefault();
        this.dialog.focus({ preventScroll: true });
        return;
      }
      const first = candidates[0];
      const last = candidates[candidates.length - 1];
      const active = this.ownerDocument.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    resetPanels() {
      this.history = [{ key: null, panel: this.rootPanel, scrollTop: 0, scrollLeft: 0 }];
      this.showPanel(this.history[0]);
    }

    showPanel(view) {
      this.rootPanel.hidden = view.panel !== this.rootPanel;
      this.panels.forEach(panel => { panel.hidden = panel !== view.panel; });
      this.panelButtons.forEach(button => {
        button.setAttribute('aria-expanded', String(button.dataset.fafPanelOpen === view.key));
      });
      this.scroller.scrollTop = view.scrollTop;
      this.scroller.scrollLeft = view.scrollLeft;
    }

    openPanel(button) {
      const key = button.dataset.fafPanelOpen;
      const panel = this.panels.get(key);
      if (!panel || this.history.some(view => view.panel === panel)) return;
      const previous = this.history[this.history.length - 1];
      previous.scrollTop = this.scroller.scrollTop;
      previous.scrollLeft = this.scroller.scrollLeft;
      const next = { key, panel, scrollTop: 0, scrollLeft: 0, returnButton: button };
      this.history.push(next);
      this.showPanel(next);
      panel.querySelector('[data-faf-panel-title]')?.focus({ preventScroll: true });
    }

    goBack() {
      if (this.history.length < 2) return;
      const previous = this.history.pop();
      const current = this.history[this.history.length - 1];
      this.showPanel(current);
      if (previous.returnButton?.isConnected) previous.returnButton.focus({ preventScroll: true });
      // Some browsers scroll focused controls even after changing a hidden panel.
      this.scroller.scrollTop = current.scrollTop;
      this.scroller.scrollLeft = current.scrollLeft;
    }

    lockScroll() {
      if (this.scrollLock) return;
      const body = document.body;
      const root = document.documentElement;
      const capture = (element, properties) => properties.map(property => ({
        property, value: element.style.getPropertyValue(property), priority: element.style.getPropertyPriority(property)
      }));
      const stableGutter = getComputedStyle(root).scrollbarGutter?.includes('stable');
      const gap = stableGutter ? 0 : Math.max(0, window.innerWidth - root.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      this.scrollLock = {
        x: window.scrollX, y: window.scrollY,
        body: capture(body, ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right']),
        root: capture(root, ['overflow', 'overscroll-behavior'])
      };
      // Fixed positioning prevents background scrolling in iOS without cancelling touch events in the menu.
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `${-this.scrollLock.y}px`);
      body.style.setProperty('left', `${-this.scrollLock.x}px`);
      body.style.setProperty('right', '0');
      body.style.setProperty('width', '100%');
      body.style.setProperty('overflow', 'hidden');
      if (gap) body.style.setProperty('padding-right', `${padding + gap}px`);
      root.style.setProperty('overflow', 'hidden');
      root.style.setProperty('overscroll-behavior', 'none');
    }

    unlockScroll() {
      if (!this.scrollLock) return;
      const saved = this.scrollLock;
      this.scrollLock = null;
      const root = document.documentElement;
      const restore = (element, properties) => properties.forEach(({ property, value, priority }) => {
        if (value) element.style.setProperty(property, value, priority);
        else element.style.removeProperty(property);
      });
      const behavior = root.style.getPropertyValue('scroll-behavior');
      const priority = root.style.getPropertyPriority('scroll-behavior');
      restore(document.body, saved.body);
      restore(root, saved.root);
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(saved.x, saved.y);
      if (behavior) root.style.setProperty('scroll-behavior', behavior, priority);
      else root.style.removeProperty('scroll-behavior');
    }
  }

  customElements.define('faf-mobile-navigation', FAFRefinedMobileMenu);
})();
